/**
 * Vocabularies shared by the request-log AND conversations surfaces
 * (TRA-886, TRA-901).
 *
 * Their own file rather than living beside the table component: the lint
 * rule wants a module to export components or constants, not both, and
 * these are consumed by the dashboard band AND the per-model tab.
 *
 * Typed against the team endpoint's range union; the per-model requests and
 * conversations endpoints generate structurally identical ones — one option
 * list means the toggle's two views can never offer different windows.
 */

import type { TeamRequestRange } from '@/hooks/useUsage'

/**
 * The status classes the gateway understands. ONE definition: this was
 * declared by hand in both request-log hooks, and the shared options were
 * typed against one copy and cast back to the other at the call site — so
 * renaming a class on one endpoint compiled while the two surfaces drifted.
 */
export type RequestStatusFilter = '2xx' | '4xx' | '5xx'

export type RequestLogView = 'requests' | 'conversations'

/**
 * Which view a page shows. The `view` param decides when a link carries
 * one (TRA-1397). Without it: conversations first (TRA-901), unless the
 * link points into the request log (`?fallback=1` from the uptime wall,
 * TRA-923, or `?trace=` for one saved call, TRA-1412), since both only
 * mean something there. An open conversation sheet (`?conversation=`)
 * wins over those, since it is visible only on the Conversations view.
 */
export function initialLogView(searchParams: URLSearchParams): RequestLogView {
  const view = searchParams.get('view')
  if (view === 'requests' || view === 'conversations') return view
  if (searchParams.get('conversation') !== null) return 'conversations'
  if (searchParams.get('fallback') === '1' || searchParams.get('trace') !== null) return 'requests'
  return 'conversations'
}

/**
 * The next search params after the toggle moves to `view`. The window,
 * the search and the metadata filters carry over, since they mean the
 * same on both views. The page, sort and status do not: they name a
 * place in one list and a column or status class of one table.
 */
export function withLogView(prev: URLSearchParams, view: RequestLogView): URLSearchParams {
  const params = new URLSearchParams(prev)
  params.set('view', view)
  for (const key of ['page', 'sort', 'status']) params.delete(key)
  return params
}

/**
 * The next search params with one param set, or dropped when `value` is
 * null. Every other param (filters, window, the other sheet's key) stays.
 */
export function withSearchParam(
  prev: URLSearchParams,
  key: string,
  value: string | null,
): URLSearchParams {
  const params = new URLSearchParams(prev)
  if (value === null) params.delete(key)
  else params.set(key, value)
  return params
}

// 20 keeps the log skimmable on the dashboard; the pager reaches the rest.
export const REQUEST_PAGE_SIZE = 20

export const REQUEST_RANGE_OPTIONS: { value: TeamRequestRange; label: string }[] = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '48h', label: 'Last 48 hours' },
  { value: '5d', label: 'Last 5 days' },
  { value: '7d', label: 'Last week' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last month' },
  { value: 'all', label: 'All time' },
]

export const REQUEST_STATUS_OPTIONS: {
  value: RequestStatusFilter | 'all'
  label: string
}[] = [
  { value: 'all', label: 'All' },
  { value: '2xx', label: '2xx' },
  { value: '4xx', label: '4xx' },
  { value: '5xx', label: '5xx' },
]

